import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type JSX,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import type { AlertModalDetail } from '~/utils/alert-modal.ts';

export const ALERT_MODAL_Z_INDEX = 20000;

type AlertModalProps = AlertModalDetail & {
  closeIconUrl: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Focuses the cancel button on mount and returns focus to the trigger on unmount.
 */
function useAlertModalFocus(
  cancelButtonRef: RefObject<HTMLButtonElement>
): void {
  useLayoutEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    cancelButtonRef.current?.focus();

    return () => {
      previouslyFocused?.focus();
    };
  }, [cancelButtonRef]);
}

const buttonBaseStyle: CSSProperties = {
  fontFamily: 'var(--alert_modal_button_font_family_resolved, Ubuntu-Bold)',
  fontSize: 'var(--alert_modal_button_font_size_resolved, 16px)',
  lineHeight: 'var(--alert_modal_button_line_height_resolved, 20px)',
  letterSpacing: 'var(--alert_modal_button_letter_spacing_resolved, -0.2px)',
  textTransform:
    'var(--alert_modal_button_text_transform_resolved, none)' as CSSProperties['textTransform'],
  paddingTop: 'var(--alert_modal_button_padding_top_resolved, 14px)',
  paddingRight: 'var(--alert_modal_button_padding_right_resolved, 20px)',
  paddingBottom: 'var(--alert_modal_button_padding_bottom_resolved, 14px)',
  paddingLeft: 'var(--alert_modal_button_padding_left_resolved, 20px)',
};

/**
 * Theme-driven Alert Modal UI. Contextual copy is provided by the caller.
 */
export function AlertModal({
  title,
  message,
  primaryButtonLabel,
  secondaryButtonLabel,
  closeIconUrl,
  onConfirm,
  onCancel,
}: AlertModalProps): JSX.Element {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  useAlertModalFocus(cancelButtonRef);

  const modal = (
    <div
      className="alert-modal-responsive_vars fixed inset-0 flex items-center justify-center bg-alert_modal_scrim_color"
      style={{ zIndex: ALERT_MODAL_Z_INDEX }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-modal-title"
        aria-describedby="alert-modal-message"
        className="mx-4 w-full max-w-md overflow-hidden rounded-alert-modal border-solid bg-alert_modal_background_color shadow-alert-modal"
        style={{
          borderWidth: 'var(--alert_modal_border_size, 1px)',
          borderColor:
            'var(--alert_modal_border_color, rgba(139, 139, 139, 0.1))',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-center justify-between gap-3 px-5 py-3"
          style={{
            paddingLeft:
              'var(--alert_modal_buttons_container_padding_left, 20px)',
            paddingRight:
              'var(--alert_modal_buttons_container_padding_right, 20px)',
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor:
              'var(--alert_modal_title_border_bottom_color, rgba(0, 0, 0, 0))',
          }}
        >
          <h2
            id="alert-modal-title"
            className="text-alert_modal_title_font_color min-w-0 flex-1"
            style={{
              fontFamily: 'var(--alert_modal_title_font_family, Ubuntu-Medium)',
              fontSize: 'var(--alert_modal_title_font_size, 18px)',
              lineHeight: 'var(--alert_modal_title_line_height, 32px)',
              letterSpacing: 'var(--alert_modal_title_letter_spacing, -0.2px)',
              textTransform:
                'var(--alert_modal_title_text_transform, none)' as CSSProperties['textTransform'],
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center bg-alert_modal_close_button_default_background_color hover:bg-alert_modal_close_button_hover_background_color"
            onClick={onCancel}
          >
            <img src={closeIconUrl} alt="" className="h-5 w-5" />
          </button>
        </div>
        <p
          id="alert-modal-message"
          className="text-alert_modal_message_font_color"
          style={{
            paddingTop: 'var(--alert_modal_message_padding_top_resolved, 16px)',
            paddingRight:
              'var(--alert_modal_message_padding_right_resolved, 20px)',
            paddingBottom:
              'var(--alert_modal_message_padding_bottom_resolved, 16px)',
            paddingLeft:
              'var(--alert_modal_message_padding_left_resolved, 20px)',
            fontFamily:
              'var(--alert_modal_message_font_family_resolved, Ubuntu-Regular)',
            fontSize: 'var(--alert_modal_message_font_size_resolved, 16px)',
            lineHeight: 'var(--alert_modal_message_line_height_resolved, 22px)',
            letterSpacing:
              'var(--alert_modal_message_letter_spacing_resolved, -0.1px)',
            textTransform:
              'var(--alert_modal_message_text_transform_resolved, none)' as CSSProperties['textTransform'],
          }}
        >
          {message}
        </p>
        <div
          className="flex"
          style={{
            justifyContent:
              'var(--alert_modal_buttons_container_alignment, flex-end)',
            columnGap:
              'var(--alert_modal_buttons_container_horizontal_gutter, 8px)',
            paddingTop:
              'var(--alert_modal_buttons_container_padding_top, 20px)',
            paddingRight:
              'var(--alert_modal_buttons_container_padding_right, 20px)',
            paddingBottom:
              'var(--alert_modal_buttons_container_padding_bottom, 20px)',
            paddingLeft:
              'var(--alert_modal_buttons_container_padding_left, 20px)',
          }}
        >
          <button
            ref={cancelButtonRef}
            type="button"
            className="text-alert_modal_secondary_button_default_font_color hover:text-alert_modal_secondary_button_focus_font_color focus-visible:text-alert_modal_secondary_button_focus_font_color disabled:text-alert_modal_secondary_button_inactive_font_color rounded-alert-modal-button bg-alert_modal_secondary_button_default_background_color hover:bg-alert_modal_secondary_button_focus_background_color focus-visible:bg-alert_modal_secondary_button_focus_background_color disabled:bg-alert_modal_secondary_button_inactive_background_color"
            style={buttonBaseStyle}
            onClick={onCancel}
          >
            {secondaryButtonLabel}
          </button>
          <button
            type="button"
            className="text-alert_modal_primary_button_default_font_color hover:text-alert_modal_primary_button_focus_font_color focus-visible:text-alert_modal_primary_button_focus_font_color disabled:text-alert_modal_primary_button_inactive_font_color rounded-alert-modal-button bg-alert_modal_primary_button_default_background_color hover:bg-alert_modal_primary_button_focus_background_color focus-visible:bg-alert_modal_primary_button_focus_background_color disabled:bg-alert_modal_primary_button_inactive_background_color"
            style={buttonBaseStyle}
            onClick={onConfirm}
          >
            {primaryButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modal;
  }

  return createPortal(modal, document.body);
}
